import type { CanvasRenderingContext2D as CanvasMod2DCtx, ImageData } from 'canvas';
import { Canvas, createCanvas, Image, loadImage } from 'canvas';

export type CanvasModCanvas = Canvas;
export type CanvasModImage = Image;
export type CanvasModImgData = ImageData;

/**
 * [Server-side focused] This class relies npm packages to create a '2D' canvas to perform it's data conversion.
 */
export class ServerTextureUtils {
  // Stateful functionality -----------------------------

  private static canvas?: Canvas;
  private static ctx2D?: CanvasMod2DCtx;

  private static CreateCanvas2DCached(width: number, height: number): void {
    if (this.canvas && this.ctx2D) {
      this.ClearCanvas();
      this.ResizeCanvas(width, height);
      return;
    }

    this.canvas = createCanvas(width, height);
    this.ctx2D = this.canvas.getContext('2d');

    this.ResizeCanvas(width, height);
  }

  private static ResizeCanvas(width: number, height: number): void {
    if (!this.canvas) { return; }

    this.canvas.width = width;
    this.canvas.height = height;
  }

  private static ClearCanvas(): void {
    if (!this.canvas) { return; }

    this.ctx2D?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * It's important to use the returned ImageData width and height after acquiring it here,
   * as it's based on the HTML image's intrinsic dimensions, not those altered by css/styling.
   * @param image
   * @returns
   */
  public static GetImageDataFromImage(image: Image): CanvasModImgData {
    this.CreateCanvas2DCached(image.naturalWidth, image.naturalHeight);

    if (!this.ctx2D) { throw new Error(`Could not create 2D context from "canvas" module`); }

    this.ctx2D.drawImage(image, 0, 0);
    const imgData: CanvasModImgData = this.ctx2D.getImageData(0, 0, image.naturalWidth, image.naturalHeight);

    // console.log(imgData);
    return imgData;
  }

  public static GetImageFromImageData(imageData: CanvasModImgData): Image {
    // * Get CanvasModImgData object by loading image to it's own canvas
    this.CreateCanvas2DCached(imageData.width, imageData.height);

    if (!this.canvas || !this.ctx2D) { throw new Error(`ServerTextureUtils::GetImageFromImageData: Canvas and context must be established.`); }

    this.ctx2D.putImageData(imageData, 0, 0);
    const image = new Image();
    image.src = this.canvas.toDataURL();
    image.width = imageData.width;
    image.height = imageData.height;

    return image;
  }

  public static GetImageDataFromPixels(pixelData: Uint8Array, width: number, height: number): CanvasModImgData {
    this.CreateCanvas2DCached(width, height);

    if (!this.canvas || !this.ctx2D) { throw new Error(`ServerTextureUtils::GetImageDataFromPixels: Canvas and context must be established.`); }

    // flip the image
    const bytesPerRow = this.canvas.width * 4;
    const temp = new Uint8Array(bytesPerRow);
    for (let y = 0; y < this.canvas.height / 2; y += 1) {
      const topOffset = y * bytesPerRow;
      const bottomOffset = (this.canvas.height - y - 1) * bytesPerRow;
      temp.set(pixelData.subarray(topOffset, topOffset + bytesPerRow));
      pixelData.copyWithin(topOffset, bottomOffset, bottomOffset + bytesPerRow);
      pixelData.set(temp, bottomOffset);
    }

    const imgData: CanvasModImgData = this.ctx2D.createImageData(this.canvas.width, this.canvas.height);
    imgData.data.set(pixelData);

    return imgData;
  }

  public static DestroyCanvas() {
    if (!this.canvas) { return; }

    this.canvas = undefined;
    this.ctx2D = undefined;
  }

  // Stateless functionality -----------------------------

  public static CreateCanvas2D(width: number, height: number): [Canvas, any] {
    const canvas: Canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');

    return [canvas, ctx];
  }

  public static async FetchImage(path: string): Promise<Image> {
    // console.log(image);
    return loadImage(path);
  }

  public static async FetchImageData(path: string): Promise<CanvasModImgData> {
    const image: Image = await this.FetchImage(path);
    return this.GetImageDataFromImageStateless(image);
  }

  public static GetImageDataFromImageStateless(image: Image): CanvasModImgData {
    const [_, ctx] = this.CreateCanvas2D(image.naturalWidth, image.naturalHeight);
    ctx.drawImage(image, 0, 0);
    const imgData: CanvasModImgData = ctx.getImageData(0, 0, image.naturalWidth, image.naturalHeight);
    return imgData;
  }

  public static GetImageDataFromPixelsWithCtx2d(pixelData: Uint8Array, ctx2d: CanvasMod2DCtx, width: number, height: number): CanvasModImgData {
    // flip the image

    const bytesPerRow = width * 4;
    const temp = new Uint8Array(bytesPerRow);
    for (let y = 0; y < height / 2; y += 1) {
      const topOffset = y * bytesPerRow;
      const bottomOffset = (height - y - 1) * bytesPerRow;
      temp.set(pixelData.subarray(topOffset, topOffset + bytesPerRow));
      pixelData.copyWithin(topOffset, bottomOffset, bottomOffset + bytesPerRow);
      pixelData.set(temp, bottomOffset);
    }

    const imgData: CanvasModImgData = ctx2d.createImageData(width, height);
    imgData.data.set(pixelData);

    return imgData;
  }
}
